"use client";

import { ChatboxColors, Crisp } from "crisp-sdk-web";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import {
	claritySetTag,
	clarityTrack,
	clarityUpgradeSession,
} from "@/lib/clarity";
import { posthog } from "@/lib/posthog";
import { chatwootEstaAtivo } from "@/lib/support/provedor-ativo";

/**
 * CrispLoader — único ponto de inicialização do Crisp chat.
 *
 * Responsabilidades:
 *   1. Configurar e carregar Crisp (defer pra não bloquear LCP)
 *   2. Anexar event listeners — chat opened/closed/initiated, mensagem
 *      enviada/recebida — forward pra PostHog (eventos) e Clarity
 *      (upgrade session no engajamento de suporte)
 *   3. Bridge Crisp ↔ Clarity — anexar session/user IDs do Clarity
 *      como custom data no Crisp pra link direto da gravação
 *
 * Não roda em /auth/* (chat só faz sentido logado).
 */
export function CrispLoader() {
	const pathname = usePathname();
	const isAuthRoute = pathname?.includes("/auth");

	useEffect(() => {
		// CHAT002-4226: com o Chatwoot atendendo, o Crisp não carrega (a conta
		// foi suspensa). Nunca dois widgets na mesma tela.
		if (isAuthRoute || chatwootEstaAtivo()) return;

		const websiteId = process.env.NEXT_PUBLIC_CRISP_TOKEN;
		if (!websiteId) {
			if (process.env.NODE_ENV !== "production") {
				console.warn("[crisp] NEXT_PUBLIC_CRISP_TOKEN não definido");
			}
			return;
		}

		const initCrisp = () => {
			Crisp.configure(websiteId, {
				autoload: false,
				safeMode: process.env.NODE_ENV !== "production",
				sessionMerge: true,
			});

			Crisp.load();

			Crisp.setAvailabilityTooltip(false);

			// ─── Branding & comportamento ────────────────────────────
			// hide_on_away é gerenciado pelo painel admin Crisp + regra do front
			// que oculta fora do horário — não sobrescrevemos via SDK.
			try {
				// Tema verde — alinha com identidade visual ChatADV
				Crisp.setColorTheme(ChatboxColors.Green);

				// Mostra "X operadores online" — aumenta confiança no tempo de resposta
				Crisp.toggleOperatorCount(true);

				// Mantém visível em mobile — advogados usam muito no celular
				Crisp.setHideOnMobile(false);
			} catch (error) {
				if (process.env.NODE_ENV !== "production") {
					console.warn("[crisp] erro ajustando branding/comportamento", error);
				}
			}

			// ─── Event listeners ──────────────────────────────────────
			// Forward pra PostHog + Clarity. Clarity upgrade marca a sessão
			// como prioridade máxima (engajamento de suporte = sinal valioso).

			try {
				Crisp.session.onLoaded(() => {
					posthog?.capture("crisp_session_loaded");
				});

				Crisp.chat.onChatInitiated(() => {
					posthog?.capture("crisp_chat_initiated");
					clarityUpgradeSession("crisp_chat_initiated");
					clarityTrack("crisp_chat_initiated");
					claritySetTag("support_engagement", "initiated");
				});

				Crisp.chat.onChatOpened(() => {
					posthog?.capture("crisp_chat_opened");
					clarityTrack("crisp_chat_opened");
					claritySetTag("support_engagement", "opened");
					window.dispatchEvent(new CustomEvent("crisp:chat:opened"));
				});

				Crisp.chat.onChatClosed(() => {
					posthog?.capture("crisp_chat_closed");
					clarityTrack("crisp_chat_closed");
					window.dispatchEvent(new CustomEvent("crisp:chat:closed"));
				});

				Crisp.message.onMessageSent(() => {
					posthog?.capture("crisp_message_sent");
					clarityUpgradeSession("crisp_message_sent");
					clarityTrack("crisp_message_sent");
					claritySetTag("support_engagement", "message_sent");
					window.dispatchEvent(new CustomEvent("crisp:message:sent"));
				});

				Crisp.message.onMessageReceived(() => {
					posthog?.capture("crisp_message_received");
					clarityTrack("crisp_message_received");
					window.dispatchEvent(new CustomEvent("crisp:message:received"));
				});

				// User começou a digitar mas não enviou — sinal de intent.
				// Útil pra detectar fricção (pergunta que abandonou) e
				// medir engajamento que não vira ticket fechado.
				Crisp.message.onMessageComposeSent(() => {
					posthog?.capture("crisp_message_compose_sent");
					clarityTrack("crisp_message_compose_sent");
				});

				Crisp.onWebsiteAvailabilityChanged((available: boolean) => {
					posthog?.capture("crisp_availability_changed", { available });
					claritySetTag(
						"support_availability",
						available ? "online" : "offline"
					);
					// Expõe a disponibilidade REAL de operadores para a UI decidir
					// entre abrir o chat ao vivo ou cair no fluxo "deixe mensagem".
					// Cache síncrono em window + evento para consumidores reativos.
					(
						window as Window & { __crispOperatorAvailable?: boolean }
					).__crispOperatorAvailable = available;
					window.dispatchEvent(
						new CustomEvent("crisp:availability:changed", {
							detail: { available },
						})
					);
				});
			} catch (error) {
				if (process.env.NODE_ENV !== "production") {
					console.warn("[crisp] erro registrando event listeners", error);
				}
			}

			// ─── Bridge Clarity ↔ Crisp ──────────────────────────────
			// Anexa session/user IDs do Clarity como custom data no Crisp.
			// Operador de suporte vê link direto pra gravação no Clarity ao
			// abrir o ticket. Retry com backoff (Clarity via GTM leva 2-5s).
			const attachClaritySessionInfo = () => {
				const clarity = (
					window as {
						clarity?: (
							cmd: string,
							key: string,
							cb: (val: string) => void
						) => void;
					}
				).clarity;
				if (typeof clarity !== "function") return false;

				try {
					const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
					clarity("get", "sessionId", (sessionId: string) => {
						if (!sessionId) return;
						const recordingUrl = projectId
							? `https://clarity.microsoft.com/projects/view/${projectId}/impressions?SessionId=is;${sessionId}`
							: undefined;
						Crisp.session.setData({
							clarity_session_id: sessionId,
							...(recordingUrl ? { clarity_recording_url: recordingUrl } : {}),
						});
					});

					clarity("get", "userId", (userId: string) => {
						if (!userId) return;
						Crisp.session.setData({ clarity_user_id: userId });
					});
					return true;
				} catch {
					return false;
				}
			};

			const tryAttach = (attempt = 0) => {
				if (attachClaritySessionInfo()) return;
				if (attempt >= 5) return;
				setTimeout(() => tryAttach(attempt + 1), 2000);
			};
			tryAttach();

			// ─── Sync de theme (light/dark) ──────────────────────────
			// Crisp SDK não expõe color:mode tipado — usar raw $crisp.push.
			// Observer escuta mudanças na classe do <html> e propaga.
			const syncCrispTheme = () => {
				try {
					const isDark = document.documentElement.classList.contains("dark");
					const $crisp = (window as { $crisp?: Array<unknown[]> }).$crisp;
					if (!$crisp || typeof $crisp.push !== "function") return;
					$crisp.push(["config", "color:mode", [isDark ? "dark" : "light"]]);
				} catch {
					// Silencioso: Crisp pode não estar carregado
				}
			};

			syncCrispTheme();

			const themeObserver = new MutationObserver(() => {
				syncCrispTheme();
			});
			themeObserver.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ["class"],
			});
		};

		// Defer: carrega após a página estar interativa
		let fallbackTimer: ReturnType<typeof setTimeout>;
		let idleHandle: number | undefined;

		if (typeof window.requestIdleCallback === "function") {
			idleHandle = window.requestIdleCallback(initCrisp, { timeout: 5000 });
		} else {
			fallbackTimer = setTimeout(initCrisp, 5000);
		}

		return () => {
			if (
				idleHandle !== undefined &&
				typeof window.cancelIdleCallback === "function"
			) {
				window.cancelIdleCallback(idleHandle);
			}
			clearTimeout(fallbackTimer);
		};
	}, [isAuthRoute]);

	return null;
}
